import { updateHiddenComponents } from 'components/practice/charts/ClinicalNote/MyScribeAI/Templates/shared/lib/helpers';

export const getNonEmptyFields = (obj) => {
  const result = {};

  for (const key in obj) {
    if (
      [
        'user_id',
        'practitioner_id',
        'practice_id',
        'updated_at',
        'created_at',
        'state',
        'appointment_id',
        'cpt_codes',
        'icd_10_codes',
        'goals',
        'lock_history',
        'charts3d',
        'treatment_plan_id',
        'vitals'
      ].includes(key)
    ) {
      continue; // Skip keys that are in the excluded list
    }
    const value = obj[key];

    if (value !== null && value !== '' && value !== undefined) {
      // Recursively filter nested objects or arrays
      if (Array.isArray(value)) {
        if (value.length > 0) {
          result[key] = value;
        }
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        const nestedResult = getNonEmptyFields(value);
        if (Object.keys(nestedResult).length > 0) {
          result[key] = nestedResult;
        }
      } else {
        result[key] = value;
      }
    }
  }
  return result;
};

export const filterTypeOfNotes = (typeOfNotes, titles) => {
  const finalTitles = titles.map((title) => {
    switch (title) {
      case 'present_illness':
        return 'patient-needs';
      case 'other_complaints':
        return 'complaints';
      case 'cpt_codes':
        return 'cpt-codes';
      case 'icd_10_codes':
        return 'diagnosis-codes';
      case 'charts3d':
        return 'face';
      default:
        return title;
    }
  });

  const filteredNotes = {};
  for (const noteType in typeOfNotes) {
    const note = typeOfNotes[noteType];
    const { components } = note;

    const filteredComponents = {};
    for (const componentKey in components) {
      const component = components[componentKey];

      if (finalTitles.includes(componentKey) || finalTitles.includes(component.path)) {
        filteredComponents[componentKey] = { ...component, checked: true, selected: true };
      } else {
        filteredComponents[componentKey] = { ...component, checked: false, selected: false };
      }
    }

    if (Object.keys(filteredComponents).length > 0) {
      filteredNotes[noteType] = {
        ...note,
        components: filteredComponents,
        checked: true
      };
    }
  }

  return filteredNotes;
};

export const getFinalAdvancedForms = (filledAdvancedForms, advancedForms) => {
  const advancedFormsMap = new Map();
  advancedForms.forEach((form) => {
    advancedFormsMap.set(form.id, form);
  });

  const finalAdvancedForms = filledAdvancedForms?.map((filledForm) => {
    const matchedForm = advancedFormsMap.get(filledForm.form_id);

    if (matchedForm) {
      return {
        ...filledForm,
        form_name: matchedForm.name,
        custom_title: matchedForm.custom_title,
        form_version: matchedForm.version
      };
    }

    return filledForm;
  });

  return finalAdvancedForms;
};

// Initialization function
export const initializeFilledForms = (typeOfNotes, nonEmptyTitles, nonEmptyAdvancedFormsTitles) => {
  const initialFilledTypeOfNotes = filterTypeOfNotes(typeOfNotes, [
    ...nonEmptyTitles,
    ...(nonEmptyAdvancedFormsTitles || [])
  ]);
  return updateHiddenComponents(initialFilledTypeOfNotes, true);
};

// Function to extract advanced forms
export const extractAdvancedForms = (advancedHP, advancedSOAP, customFormTypes) => {
  const customAdvancedForms = Object.values(customFormTypes || {}).flat();
  return [...advancedHP, ...advancedSOAP, ...customAdvancedForms];
};

export const getInitialFormsState = ({
  typeOfNotes,
  nonEmptyTitles,
  nonEmptyAdvancedFormsTitles,
  enhanceNoteSettings = null
}) => {
  let initialForms = initializeFilledForms(
    typeOfNotes,
    nonEmptyTitles,
    nonEmptyAdvancedFormsTitles
  );
  if (enhanceNoteSettings) {
    const enabledForms = {};
    for (const key in typeOfNotes) {
      const type = enhanceNoteSettings[key] || {};
      enabledForms[key] = { ...initialForms[key], checked: !!type?.enabled };
      const forms = type?.forms || {};
      for (const formKey in forms) {
        if (enabledForms?.[key]?.['components']?.[formKey]) {
          enabledForms[key]['components'][formKey] = {
            ...(initialForms?.[key]?.components?.[formKey] || {}),
            selected: !!type?.forms?.[formKey]?.enabled,
            checked: !!type?.forms?.[formKey]?.checked
          };
        }
      }
    }
    initialForms = enabledForms;
  }
  return initialForms;
};
